import React from 'react'
import { useDropzone } from 'react-dropzone'
import { ActionIcon, Anchor, Group, Loader, Stack, Text } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { TrashIcon } from '@radix-ui/react-icons'

import {
  acceptedDeliverables,
  deliverableExtensions,
  fileDeliverable,
  INVOICE,
  TRANSCRIPTS,
} from './deliverables'
import { CourtReporterPageEvent } from './types'

import {
  CourtReporterDeliverable,
  CourtReporterDeliverableType,
} from '@/apiClient'
import { Card } from '@/components/Event/Detail/components/Card'
import { useModals } from '@/hooks/useModals'
import {
  useDeliverablesQuery,
  useRemoveDeliverableMutation,
  useUploadDeliverableMutation,
} from '@/queries/courtReporters'
import { getApiErrorMessage } from '@/utils/apiError'

type DeliverablesEvent = { deliverables_url: string }

const DELIVERABLE_LABELS: Record<CourtReporterDeliverableType, string> = {
  invoice: 'Invoice',
  transcript_pdf: 'Transcript PDF',
  transcript_txt: 'Transcript TXT',
}

export const DocumentsCard: React.FC<{
  event: CourtReporterPageEvent
  isAdmin?: boolean
}> = ({ event, isAdmin = false }) => {
  const splittingNotice = event.is_splitting
    ? 'You can upload a transcript for each witness once the event is split by witness.'
    : undefined
  const transcriptNotice =
    splittingNotice ??
    (event.is_waiting_for_reporter_feedback
      ? "You can upload the transcript once the event's feedback is submitted."
      : undefined)

  return (
    <Card heading="Documents">
      <Stack>
        {!event.is_split_result && (
          <DocumentSection
            title="Invoice"
            event={event}
            deliverables={INVOICE}
            accepted={acceptedDeliverables(event)}
            isAdmin={isAdmin}
          />
        )}

        {event.witness_events.length > 0
          ? event.witness_events.map(witnessEvent => (
              <DocumentSection
                key={witnessEvent.id}
                title={`Transcript – ${witnessEvent.witness_name ?? 'Witness'}`}
                event={witnessEvent}
                deliverables={TRANSCRIPTS}
                accepted={TRANSCRIPTS}
                isAdmin={isAdmin}
                notice={
                  witnessEvent.status === 'cancelled'
                    ? 'The deposition of this witness was canceled, so there is no transcript.'
                    : splittingNotice
                }
              />
            ))
          : event.status !== 'split' && (
              <DocumentSection
                title="Transcript"
                event={event}
                deliverables={TRANSCRIPTS}
                accepted={acceptedDeliverables(event)}
                isAdmin={isAdmin}
                notice={transcriptNotice}
              />
            )}
      </Stack>
    </Card>
  )
}

const DocumentSection: React.FC<{
  title: string
  event: DeliverablesEvent
  deliverables: CourtReporterDeliverableType[]
  accepted: CourtReporterDeliverableType[]
  isAdmin: boolean
  notice?: string
}> = ({ title, event, deliverables, accepted, isAdmin, notice }) => {
  const { data: uploaded, isLoading, isError } = useDeliverablesQuery(event)

  // Ops also see what the event doesn't take, such as an invoice a split copied onto a witness.
  const listed = (uploaded ?? []).filter(
    d =>
      deliverables.includes(d.deliverable) ||
      (isAdmin && !accepted.includes(d.deliverable)),
  )
  const missing = deliverables.filter(
    d => accepted.includes(d) && !uploaded?.some(u => u.deliverable === d),
  )

  return (
    <Stack spacing="xs">
      <Text size="sm" weight={600}>
        {title}
      </Text>

      {isLoading ? (
        <Loader size="sm" />
      ) : isError ? (
        <Text size="sm" color="red">
          Could not load the documents.
        </Text>
      ) : (
        <>
          {listed.map(deliverable => (
            <UploadedDeliverable
              key={deliverable.id}
              event={event}
              deliverable={deliverable}
              canRemove={isAdmin}
            />
          ))}

          {notice ? (
            <Text size="sm" color="dimmed">
              {notice}
            </Text>
          ) : (
            missing.length > 0 && (
              <DeliverableDropzone event={event} deliverables={missing} />
            )
          )}
        </>
      )}
    </Stack>
  )
}

const UploadedDeliverable: React.FC<{
  event: DeliverablesEvent
  deliverable: CourtReporterDeliverable
  canRemove: boolean
}> = ({ event, deliverable, canRemove }) => {
  const { openConfirmModal } = useModals()
  const { mutate: remove, isLoading } = useRemoveDeliverableMutation(event)
  const label = DELIVERABLE_LABELS[deliverable.deliverable]

  return (
    <Group position="apart" noWrap className="rounded bg-gray-100 p-3">
      <Text size="sm">
        {label}:{' '}
        <Anchor href={deliverable.url} target="_blank">
          {deliverable.original_name ?? `${label}.${deliverable.format}`}
        </Anchor>
      </Text>

      {canRemove &&
        (isLoading ? (
          <Loader size="sm" />
        ) : (
          <ActionIcon
            aria-label={`Remove ${label}`}
            color="red"
            onClick={() =>
              openConfirmModal({
                title: `Remove the ${label.toLowerCase()}?`,
                children: (
                  <Text size="sm">
                    The file is removed from the event as in Back Office.
                  </Text>
                ),
                labels: { confirm: 'Remove', cancel: 'Cancel' },
                confirmProps: { color: 'red' },
                onConfirm: () =>
                  remove(deliverable, {
                    onError: error =>
                      showNotification({
                        color: 'red',
                        message: getApiErrorMessage(
                          error,
                          'Could not remove the document',
                        ),
                      }),
                  }),
              })
            }
          >
            <TrashIcon />
          </ActionIcon>
        ))}
    </Group>
  )
}

const DeliverableDropzone: React.FC<{
  event: DeliverablesEvent
  deliverables: CourtReporterDeliverableType[]
}> = ({ event, deliverables }) => {
  const { mutate: upload, isLoading } = useUploadDeliverableMutation(event)
  const extensions = deliverableExtensions(deliverables)
  const fileTypes = extensions.map(e => e.slice(1).toUpperCase()).join(', ')

  const { getRootProps, getInputProps } = useDropzone({
    accept: extensions,
    multiple: false,
    disabled: isLoading,
    onDropAccepted: ([file]) => {
      const target = fileDeliverable(file, deliverables)
      if (target === null) {
        return
      }

      upload(
        { ...target, file },
        {
          onError: error =>
            showNotification({
              color: 'red',
              message: getApiErrorMessage(
                error,
                'Could not upload the document',
              ),
            }),
        },
      )
    },
    onDropRejected: () =>
      showNotification({
        color: 'red',
        message: `Only one ${fileTypes} file can be uploaded here.`,
      }),
  })

  return (
    <div
      {...getRootProps()}
      className="cursor-pointer rounded border border-dashed border-gray-300 p-4 text-center"
    >
      <input {...getInputProps()} />
      {isLoading ? (
        <Loader size="sm" />
      ) : (
        <Text size="sm" color="dimmed">
          Drop a {fileTypes} file here or click to choose one
        </Text>
      )}
    </div>
  )
}
